import initializeOptions from 'Map/configurer/initializeTrafficIncidentsOptions';
import trafficIncidentDetailsTier from 'Map/tier/trafficIncidentDetailsTier';
import trafficIncidentTilesTier from 'Map/tier/trafficIncidentTilesTier';

const POSITION_TRAFFIC_INCIDENTS = 3;
export const ID = 'trafficIncidents';

/**
 * This tier provides both
 * <a href="https://developer.tomtom.com/traffic-api/traffic-api-documentation/traffic-incidents">traffic incidents</a>
 *  details and tiles information.
 *
 * ```javascript
 * var map = tt.map({
 *  //your map configuration
 *});
 * map.on('load', function() {
 *     map.addTier(new tt.TrafficIncidentTier({
 *         key: <api key>,//your traffic incidents api key goes here
 *         incidentDetails: true, //or provide more detailed configuration
 *         incidentTiles: true //or provide more detailed configuration
 *     }))
 * });
 *```
 *
 * @class TrafficIncidentTier
 * @namespace Maps
 */

export class TrafficIncidentTier {
    /**
     * @method constructor
     *
     * @param {Object} options Traffic incidents options.
     * @param {String} options.key Traffic incident api key. This key is shared between traffic incident details and
     * tiles services.
     * @param {Boolean|Object} [options.incidentDetails] Traffic incident details - pass true to display incident
     * details or an object with options.
     * @param {String} [options.incidentDetails.style] Style of traffic incidents details (may change their position).
     * Supported values are: `s1`, `s2` and `s3`.
     * @param {String} [options.incidentDetails.markerOptions] Marker options to for
     * {{#crossLink "Maps.IncidentMarker"}}{{/crossLink}}.
     * @param {String} [options.incidentTiles.style='tomtom://vector/1/s1'] The traffic incident tiles style.
     * It can be one of following types:
     * * Schema, you can use of following values:
     *   1. `tomtom://vector/1/s1`
     *   2. `tomtom://vector/1/s2`
     *   3. `tomtom://vector/1/s3`
     *
     *   4. `tomtom://raster/1/s1`
     *   5. `tomtom://raster/1/s2`
     *   6. `tomtom://raster/1/s3`
     * * URL to the JSON object conforming
     * to the schema described in <a href="https://docs.mapbox.com/mapbox-gl-js/style-spec/">
     * this compatible specification</a>.
     * * JSON object conforming to the mentioned specification.
     * @param {Number} [options.refresh=30000] The interval in milliseconds between subsequent refreshes of traffic
     * incident information. Any value lower than 30000 will be treated as 30000. This interval affects both traffic
     * incident details and tiles services.
     * @param {Number} [position=3] The tier's position in a map style.
     */
    constructor(options, position = POSITION_TRAFFIC_INCIDENTS) {
        this._options = initializeOptions(options);
        this._position = position;
        this._tilesTier = this._options.incidentTiles ? trafficIncidentTilesTier(this._options, position) : null;
        this._detailsTier = this._options.incidentDetails ? trafficIncidentDetailsTier(this._options, position) : null;
        this._visible = false;
    }

    getId = () => ID;
    getPosition = () => this._position;
    getStyle = () => this._tilesTier && this._tilesTier.getStyle();
    setStyle = (style) => {
        if (this._tilesTier) {
            this._tilesTier.setStyle(style);
        }
    };
    getOptions = () => this._options;
    isVisible = () => this._visible;

    onBeforeAdd = (map) => {
        if (this._tilesTier) {
            this._tilesTier.onBeforeAdd(map);
        }
    };

    onAdd = (map) => {
        if (this._tilesTier) {
            this._tilesTier.onAdd(map);
        }

        if (this._detailsTier) {
            this._detailsTier.onAdd(map);
        }
    };

    onRemove = (map) => {
        if (this._tilesTier) {
            this._tilesTier.onRemove(map);
        }

        if (this._detailsTier) {
            this._detailsTier.onRemove(map);
        }
    };

    getUrlTransformer = () => this._tilesTier && this._tilesTier.getUrlTransformer();
}



// WEBPACK FOOTER //
// ./map/tier/trafficIncidentTier.js